/**
 * The wear-out layer's authoring rules — D152.
 *
 * @remarks
 * The D31/D47 dead-content law applied to a counter: a thing that can never
 * be spent the way its pack says is a counter nothing moves. So every act in
 * `spentBy` and `refuses` must be one the item can UNDERGO — `use` wants an
 * interaction, `open_container` a door, `switch_on` a switch, `search`
 * contents, `take` portability (the wear pair is the schema's own refinement,
 * because it needs no other item to judge). Plus the ordinary walk over what
 * the crossing says and does.
 *
 * @packageDocumentation
 */

import { containerItemIdOf, type ContentPackDefinition, type ItemAct, type ItemDefinition, refusedActsOf } from '@orpheus/content-schema';

import { type ReferenceBrokenFailure } from './load-failure.js';
import { checkNarrativeLines, declaredIdSetsOf } from './reference-walkers.js';
import { brokenReference } from './semantic-validation.js';

/**
 * Whether one act can ever happen to one item, and if not, why — a table so
 * that a new act is a compile error until its precondition is stated.
 */
const ACT_PRECONDITIONS: Readonly<
	Record<ItemAct, { readonly holds: (contentPack: ContentPackDefinition, item: ItemDefinition) => boolean; readonly wants: string }>
> = {
	close_container: { holds: (_pack, item) => item.openable !== undefined, wants: 'a door (`openable`)' },
	drop: { holds: (pack, item) => item.isPortable && !pack.ambientItemIds.includes(item.itemId), wants: 'to be portable' },
	// The wear pair is judged by the schema's own refinement; here it always holds.
	equip: { holds: () => true, wants: '' },
	inspect: { holds: () => true, wants: '' },
	open_container: { holds: (_pack, item) => item.openable !== undefined, wants: 'a door (`openable`)' },
	search: {
		holds: (pack, item) => pack.items.some((other) => containerItemIdOf(other.containedIn) === item.itemId),
		wants: 'something inside it to find (another item `containedIn` it)',
	},
	switch_off: { holds: (_pack, item) => item.switchable !== undefined, wants: 'a switch (`switchable`)' },
	switch_on: { holds: (_pack, item) => item.switchable !== undefined, wants: 'a switch (`switchable`)' },
	take: { holds: (pack, item) => item.isPortable && !pack.ambientItemIds.includes(item.itemId), wants: 'to be portable' },
	unequip: { holds: () => true, wants: '' },
	use: { holds: (_pack, item) => item.useInteractions.length > 0, wants: 'at least one `useInteractions` entry (it is the INSTRUMENT that wears)' },
};

/**
 * Validate every durability declaration.
 *
 * @param contentPack - The parsed pack
 * @returns Every failure found
 */
export const validateDurabilityAuthoring = (contentPack: ContentPackDefinition): readonly ReferenceBrokenFailure[] => {
	const failures: ReferenceBrokenFailure[] = [];
	const declared = declaredIdSetsOf(contentPack);

	contentPack.items.forEach((item, itemIndex) => {
		const { durability } = item;

		if (durability === undefined) {
			return;
		}

		const path = `items[${String(itemIndex)}].durability`;
		const judge = (acts: readonly ItemAct[], field: string): void => {
			acts.forEach((act, actIndex) => {
				const precondition = ACT_PRECONDITIONS[act];

				if (!precondition.holds(contentPack, item)) {
					failures.push(
						brokenReference(
							`${path}.${field}[${String(actIndex)}]`,
							act,
							`this act can never happen to the item — \`${act}\` wants ${precondition.wants}, so the counter it names would never move`,
						),
					);
				}
			});
		};

		judge(durability.spentBy, 'spentBy');

		if (durability.refuses !== undefined) {
			judge(refusedActsOf(durability), 'refuses');
		}

		checkNarrativeLines(durability.spentNarratives, `${path}.spentNarratives`, declared, failures);
		checkNarrativeLines(durability.spentRefusalNarratives, `${path}.spentRefusalNarratives`, declared, failures);
	});

	return failures;
};
